import type { EffortLevel, ModelEffortRule } from "./effort";

export type CredentialField = {
  key: string;
  label: string;
  description: string;
  placeholder: string;
  sensitive?: boolean;
  multiline?: boolean;
  required?: boolean;
};

export type PackedCredentials = {
  baseUrl: string;
  apiKey: string;
};

type AttachmentSupport = {
  documentMediaTypes: readonly string[];
  maxImageDimension: number;
  modelMediaTypeOverrides?: Readonly<Record<string, string>>;
  // Media types listed here are inlined into the user message as text content
  // instead of being sent as document blocks. Every provider inherits the text
  // baseline below; document blocks are the per-provider opt-in for native
  // document understanding (PDF, Office files).
  inlineTextMediaTypes?: readonly string[];
};

// Text inlining needs no provider capability — the bytes become prompt text —
// so it's the universal baseline; a dropped text file is always a coverage gap.
const TEXT_INLINE_MEDIA_TYPES = [
  "text/plain",
  "text/csv",
  "text/html",
  "text/markdown",
  "application/json",
  "application/xml",
  "image/svg+xml",
] as const;

type ProviderAttachmentSupport = {
  documentMediaTypes?: readonly string[];
  maxImageDimension: number;
  modelMediaTypeOverrides?: Readonly<Record<string, string>>;
  inlineTextMediaTypes?: readonly string[];
};

export type ReasoningReplay =
  | { via: "signature"; providerOptionsKey: string }
  | { via: "text" };

type AIProviderManifest<Id extends string = string> = {
  id: Id;
  label: string;
  description: string;
  // The endpoint the SDK hits when no base URL is set; scopes the proxy egress
  // route and connector match. Omitted for providers that require a base URL.
  defaultBaseUrl?: string;
  // The connector type offered for inline creation; omitted for providers with
  // no single-key connector (managed/bedrock, azure).
  connectorIdentifier?: string;
  // When set, the connector owns the endpoint (its `configuration.baseUrl`), so
  // the AI-provider modal hides its own Base URL field and the row stores no
  // base URL — every read derives it from the connector's current configuration.
  endpointFromConnector?: boolean;
  // For connectors that describe their endpoint some other way; Bedrock stores a
  // region, not a URL.
  connectorBaseUrl?: (configuration: Record<string, string>) => string;
  defaultChatModel: string;
  defaultFastModel: string;
  defaultChatEffort?: EffortLevel;
  modelEfforts?: readonly ModelEffortRule[];
  configurable: boolean;
  // Off the picker but still stored on existing rows; unlike `configurable:
  // false` it stays in updateAIConfig's enum, so editing those rows still saves.
  retired?: boolean;
  requiresChatModel: boolean;
  // Set only when the base URL is machine-built rather than admin-typed; absent
  // runs the SSRF check, so a new provider is guarded by default.
  skipsBaseUrlValidation?: boolean;
  credentialFields: readonly CredentialField[];
  packCredentials: (values: Record<string, string>) => PackedCredentials;
  unpackCredentials: (row: PackedCredentials) => Record<string, string>;
  subtitleField: string | null;
  contextLimitPatterns: readonly string[];
  attachmentSupport: AttachmentSupport;
  reasoningReplay?: ReasoningReplay;
};

type AIProviderManifestInput<Id extends string> = Omit<
  AIProviderManifest<Id>,
  "attachmentSupport"
> & {
  attachmentSupport: ProviderAttachmentSupport;
};

export function defineAIProviderManifest<const Id extends string>(
  manifest: AIProviderManifestInput<Id>
): AIProviderManifest<Id> {
  const native = manifest.attachmentSupport;
  return {
    ...manifest,
    attachmentSupport: {
      documentMediaTypes: native.documentMediaTypes ?? [],
      maxImageDimension: native.maxImageDimension,
      inlineTextMediaTypes: [
        ...TEXT_INLINE_MEDIA_TYPES,
        ...(native.inlineTextMediaTypes ?? []),
      ],
      modelMediaTypeOverrides: native.modelMediaTypeOverrides,
    },
  };
}

export function indexAIProvidersById<
  const Providers extends readonly AIProviderManifest[],
>(providers: Providers) {
  return Object.fromEntries(
    providers.map((provider) => [provider.id, provider])
  ) as {
    [Provider in Providers[number] as Provider["id"]]: Provider;
  };
}

export function emptyCredentials(
  fields: readonly CredentialField[]
): Record<string, string> {
  return Object.fromEntries(fields.map((field) => [field.key, ""]));
}
